import * as React from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { hasValue } from '../../../../lib/lib';
import AppForm from '../../common/AppForm';
import PageTitle from '../../common/PageTitle';
import PasswordField from '../../../../commonComponents/appForm/PasswordField';
import {
	getWrongPasswordAttemptsFromLocalStorage,
	setWrongPasswordAttemptsToLocalStorage
} from '../../../../core/localStorage';
import { PasswordModel } from '../../../../models/AccountModels';
import { InlineError } from '../../login/forms/LoginForm';
import { AuthenticationResult } from '../../../../models/TwoFactorAuthenticationResult';
import BottomModal from '../../../../commonComponents/bottomModal/BottomModal';
import { resetPasswordPageUrl, dashboardPageUrl } from '../../../../core/constants';
import { verifyPassword } from '../../../../core/api';
import { MessageTypes } from '../../../../core/enums';
import useClientContextStore from '../../../../zustandStore/clientContextStore';

const BiometricPasswordForm = ({ biometricTypeText, onSubmit }: Props) => {
	const clientDetails = useClientContextStore((state) => state.clientDetails);
	const methods = useForm();
	const {
		formState: { errors }
	} = methods;
	const [showPassword, setShowPassword] = useState(false);
	const [inlineError, setInlineError] = useState<AuthenticationResult | string>(null);
	const [openMaxAttemptPrompt, setOpenMaxAttemptPrompt] = useState(false);

	const { signal } = new AbortController();

	const handleCloseModal = () => {
		setOpenMaxAttemptPrompt(false);
	};

	const handleSubmit = async (model: PasswordModel) => {
		const isValid = await verifyPassword(model, signal);
		if (isValid) {
			onSubmit(clientDetails.email, model.password);
		} else {
			const failedAttempts = getWrongPasswordAttemptsFromLocalStorage() + 1;
			if (failedAttempts >= 5) {
				setOpenMaxAttemptPrompt(true);
				setWrongPasswordAttemptsToLocalStorage(0);
			} else {
				setWrongPasswordAttemptsToLocalStorage(failedAttempts);
				setInlineError('Invalid password. Please try again.');
			}
		}
	};

	const handleTogglePasswordMask = () => {
		setShowPassword(!showPassword);
	};

	return (
		<>
			<PageTitle
				title="Enter your password"
				subtitle={`To keep your account secure, enter your password to set up ${biometricTypeText} on this device.`}
			/>
			<AppForm methods={methods} onSubmit={handleSubmit} primaryButtonText="Continue" singleColumn>
				<PasswordField
					showPassword={showPassword}
					onTogglePasswordMask={handleTogglePasswordMask}
					required={false}
					validate={false}
					customValidate={(value: string) => hasValue(value) || 'Please enter a valid password.'}
				/>
				{inlineError && Object.keys(errors).length === 0 && <InlineError>{inlineError}</InlineError>}
				<BottomModal
					title="Too many failed attempts"
					body={[
						"You've reached the maximum password attempts. Please reset your password to try again or return to the dashboard."
					]}
					type={MessageTypes.Error}
					actions={[
						{ label: 'Reset password', internalLink: resetPasswordPageUrl },
						{ label: 'Return to dashboard', internalLink: dashboardPageUrl }
					]}
					open={openMaxAttemptPrompt}
					onClose={handleCloseModal}
				/>
			</AppForm>
		</>
	);
};

export default BiometricPasswordForm;

interface Props {
	biometricTypeText: string;
	onSubmit: (email: string, password: string) => void;
}
